import { Pressable, View, Platform } from 'react-native';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useTranslation } from 'react-i18next';

export interface MealActionButtonsProps {
  onSearch: () => void;
  onScan: () => void;
  /**
   * Enter/leave day-scoped selection mode. Omitted by callers that have no
   * selection surface; the button is also hidden when the slot has no logs,
   * since there would be nothing to pick.
   */
  onToggleSelectMode?: () => void;
  /** Whether selection mode is currently active (accent-soft fill + accent text). */
  selectModeActive?: boolean;
  /** Whether the visible slot holds any logs. */
  hasLogs?: boolean;
}

export const MealActionButtons = ({
  onSearch,
  onScan,
  onToggleSelectMode,
  selectModeActive = false,
  hasLogs = false,
}: MealActionButtonsProps) => {
  const { t } = useTranslation('meal');

  return (
    <View className="flex flex-row gap-3">
      <Pressable
        testID="meal-search-button"
        onPress={onSearch}
        className="flex-1 md:flex-none flex flex-row items-center justify-center gap-2 rounded-[10px] bg-surface-elevated px-4 py-3 active:opacity-70"
      >
        <Icon name="Search" size={18} className="text-text-muted" />
        <Text className="text-sm font-inter-medium text-text-muted">
          {t('search')}
        </Text>
      </Pressable>
      {Platform.OS !== 'web' && (
        <Pressable
          onPress={onScan}
          className="flex-1 md:flex-none flex flex-row items-center justify-center gap-2 rounded-[10px] bg-surface-elevated px-4 py-3 active:opacity-70"
        >
          <Icon name="ScanBarcode" size={18} className="text-text-muted" />
          <Text className="text-sm font-inter-medium text-text-muted">
            {t('scan')}
          </Text>
        </Pressable>
      )}
      {onToggleSelectMode && hasLogs && (
        <Pressable
          testID="meal-select-mode"
          collapsable={false}
          onPress={onToggleSelectMode}
          className={`flex-1 md:flex-none flex flex-row items-center justify-center gap-2 rounded-[10px] px-4 py-3 active:opacity-70 ${
            selectModeActive ? 'bg-accent-soft' : 'bg-surface-elevated'
          }`}
        >
          <Icon
            name="ListChecks"
            size={18}
            className={selectModeActive ? 'text-accent' : 'text-text-muted'}
          />
          <Text
            className={`text-sm font-inter-medium ${
              selectModeActive ? 'text-accent' : 'text-text-muted'
            }`}
          >
            {t('copy.selectAction')}
          </Text>
        </Pressable>
      )}
    </View>
  );
};
